import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { CardComponentProps } from 'nextstepjs';
import { useEffect, useId, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const OnboardingTourCard = ({
  step,
  currentStep,
  totalSteps,
  nextStep,
  prevStep,
  skipTour,
  arrow,
}: CardComponentProps) => {
  const { t } = useTranslation('onboarding');
  const cardRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const i18nKey = (step.content as string) || 'welcome';
  const isFirst = currentStep === 0;
  const isLast = currentStep === totalSteps - 1;
  const progress = useMemo(
    () =>
      Array.from({ length: totalSteps }, (_, index) => ({
        active: index === currentStep,
        completed: index < currentStep,
      })),
    [currentStep, totalSteps],
  );

  useEffect(() => {
    cardRef.current?.focus({ preventScroll: true });
  }, [currentStep]);

  return (
    <div
      ref={cardRef}
      role='dialog'
      aria-modal='true'
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      tabIndex={-1}
      className='w-[min(20rem,calc(100vw-2rem))] overflow-hidden rounded-lg border border-slate-200 bg-white text-slate-950 shadow-2xl outline-none ring-1 ring-black/5 focus-visible:ring-2 focus-visible:ring-sky-500 dark:border-stone-700 dark:bg-stone-950 dark:text-stone-50 dark:ring-white/10'>
      {arrow}
      <div className='flex flex-col gap-4 p-4'>
        <div className='flex items-center justify-between gap-3'>
          <p className='text-xs font-medium text-slate-500 dark:text-stone-400'>
            {currentStep + 1} / {totalSteps}
          </p>
          <div className='flex max-w-40 flex-1 justify-end gap-1' aria-hidden='true'>
            {progress.map(({ active, completed }, index) => (
              <span
                key={index}
                className={cn(
                  'h-1.5 rounded-full transition-all',
                  active ? 'w-6 bg-sky-500' : 'w-1.5',
                  completed ? 'bg-sky-300 dark:bg-sky-700' : 'bg-slate-200 dark:bg-stone-700',
                )}
              />
            ))}
          </div>
        </div>

        <div className='space-y-1.5'>
          <h3 id={titleId} className='text-base font-semibold leading-6 text-slate-950 dark:text-white'>
            {t(`tour.${i18nKey as any}.title` as any)}
          </h3>
          <p id={descriptionId} className='text-sm leading-5 text-slate-600 dark:text-stone-300'>
            {t(`tour.${i18nKey as any}.description` as any)}
          </p>
        </div>

        <div className='flex items-center justify-between gap-3'>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={skipTour}
            className='px-2 text-slate-600 hover:text-slate-950 dark:text-stone-300 dark:hover:text-white'>
            {t('controls.skip')}
          </Button>
          <div className='flex gap-2'>
            {!isFirst && (
              <Button type='button' variant='outline' size='sm' onClick={prevStep}>
                {t('controls.prev')}
              </Button>
            )}
            <Button type='button' size='sm' onClick={nextStep}>
              {isLast ? t('controls.finish') : t('controls.next')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTourCard;
